import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { Button, Container, Image } from 'react-bootstrap';

import Accessories from 'components/Accessories';
import Packages from 'components/Packages';
import AssemblySliderSelectors from 'components/AssemblySliderSelectors';
import LocalizedLink from 'components/LocalizedLink';
import RewindButton from 'components/RewindButton';
import BottomBar from 'components/BottomBar';
import { PackagesModal } from 'components/modals';
import LoadingSpinner from 'components/LoadingSpinner';
import Price from 'components/Price';
import Disclaimer from 'components/Price/Disclaimer';
import NotFound from 'components/NotFound';
import ThreeSixty from 'components/360Viewer/index';
import Leasing from 'components/Leasing';

import slugify from 'utils/slugify';
import { getActiveInteriorPicture } from 'utils/dataUtils';
import { setColor, setInterior, setWheels, updateAccessories, updateExtraPackages, setConfig } from 'store/entities/config';
import { setCompatibilityData, setErrorData } from 'store/entities/data';

import useLocale from 'hooks/useLocale';
import useBreakPoints from 'hooks/useBreakPoints';
//import useLeasingValue from 'hooks/useLeasingValue';
import useTotalPrice from 'hooks/useTotalPrice';

import { ReactComponent as FullscreenIco } from 'assets/images/icons/Fullscreen.svg';
import { ReactComponent as CloseFullscreenIco } from 'assets/images/icons/ExitFullscreen.svg';
import { ReactComponent as ArrowRight } from 'assets/images/icons/ArrowRight.svg';
import PlaceholderCar from 'assets/images/placeholder-car.png';

import { useSpecificationDataRequest, useCompatibilityDataRequest } from 'api/requests';

const Assemble = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const locale = useLocale();

    const [option, setOption] = React.useState(null);
    const [resolve, setResolve] = React.useState(null);
    const [pictures, setPictures] = React.useState([]);

    const { model: modelId, specification, colorCode, interior, wheels, extraPackages, accessories, leasingMileage } = useSelector(state => state.config);
    const { modelsData, builderData, errorData } = useSelector(state => state.data);
    const { selectedTab } = useSelector(state => state.app);

    const ENV = window.ENV[window.location.hostname] || window.ENV.default;

    const handleFullScreen = useFullScreenHandle();
    const { isSmallerThanLg } = useBreakPoints();

    const totalPrice = useTotalPrice();

    const packagesData = builderData?.options || [];
    const selectedPackages = packagesData.filter(item => extraPackages.includes(item.id)).map(item => item.code);

    const accessoriesData = builderData?.accessories || {};
    const allAccessories = Object.keys(accessoriesData).reduce((result, key) => result.concat(accessoriesData[key]), []);
    const selectedAccessories = allAccessories.filter(item => accessories.includes(item.id)).map(item => item.code);

    const imageMain = builderData?.exteriors?.find(item => item.code === colorCode)?.pictures?.main;
    const isInteriorTab = selectedTab === 'interiors';

    const currentModel = modelsData.find(item => item.id === modelId);
    const modelBackTo = currentModel ? `/${slugify(currentModel.name)}` : `/grade?id=${modelId}`;

    const handleSetLeasing = (value) => dispatch(setConfig(value));

    const resolveConfirm = () => {
        let resolvedPackages = [...extraPackages];
        let resolvedAccessories = [...accessories];
        const equipment = {
            'options': packagesData,
            'accessories': allAccessories
        };
        const apperance = {
            'exteriors': {
                value: colorCode,
                code: builderData.exterior.code
            },
            'interiors': {
                value: interior,
                code: builderData.interior.code
            },
            'wheels': {
                value: wheels,
                code: builderData.wheels.code
            }
        };
        const updateArray = (array, el, remove) => {
            if (remove) {
                const index = array.indexOf(el);
                if (~index) array.splice(index, 1);
            } else {
                array.push(el);
            }
        };
        const updateOptions = (type, item, remove) => {
            switch (type) {
                case 'options':
                    updateArray(resolvedPackages, item.id, remove);
                    break;
                case 'accessories':
                    updateArray(resolvedAccessories, item.id, remove);
                    break;
                case 'exteriors':
                    dispatch(setColor(item.code));
                    break;
                case 'wheels':
                    dispatch(setWheels(item.code));
                    break;
                case 'interiors':
                    dispatch(setInterior(item.code));
                    break;
            }
        };
        resolve.incompatibleOptions?.forEach(set => // remove conflict options
            set.forEach(item => {
                Object.keys(equipment).forEach(type => updateOptions(type, item, true));
                Object.keys(apperance).forEach(type => {
                    if (type !== option.type && apperance[type].value === item.code) updateOptions(type, apperance[type]);
                });
            })
        );
        resolve.requiredOptions?.forEach(set => // add additional options
            set.forEach(item => {
                Object.keys(apperance).forEach(type => {
                    if (builderData[type].some(el => el.code === item.code)) updateOptions(type, item);
                });
                Object.keys(equipment).forEach(type => {
                    if (equipment[type].some(el => el.id === item.id)) updateOptions(type, item);
                });
            })
        );
        updateOptions(option.type, resolve.option, option.check);
        dispatch(updateAccessories(resolvedAccessories));
        dispatch(updateExtraPackages(resolvedPackages));
        dispatch(setCompatibilityData(resolve));
        setOption(null);
        setResolve(null);
    };

    const resolveCancel = () => {
        setOption(null);
        setResolve(null);
    };

    const dataSpecification = useSpecificationDataRequest({
        params: {
            specification,
            params: {
                brand: ENV.BRAND_NAME,
                [option?.check ? 'remove' : 'add']: option?.code,
                'options.code': [
                    colorCode,
                    //interior,
                    wheels
                ].filter(item => item),
                language: locale
            }
        },
        config: {
            enabled: false,
        },
        success: (result) => {
            const pics = result.data.exterior.pictures?.['360'] || [];
            setPictures(pics);
        },
        error: (error) => {
            console.log('Specification error: ', error.message);
        }
    });

    const dataCompatibility = useCompatibilityDataRequest({
        params: {
            specification,
            params: {
                brand: ENV.BRAND_NAME,
                [option?.check ? 'remove' : 'add']: option?.code,
                'options.code': [
                    ...selectedPackages,
                    ...selectedAccessories,
                    colorCode,
                    interior,
                    wheels
                ].filter(item => item),
                language: locale
            }
        },
        config: {
            enabled: false,
        },
        success: (result) => {
            if (result.data?.requiredOptions || result.data?.incompatibleOptions) result.data.resolve = true; // need confirmation for resolving
            setResolve(result.data);
        },
        error: (error) => {
            console.log('Compatibility error: ', error.message);
        }
    });

    React.useEffect(() => {
        if (resolve && !resolve.resolve) resolveConfirm();
    }, [resolve]);

    React.useEffect(() => {
        if (option) dataCompatibility.refetch();
    }, [option]);

    React.useEffect(() => {
        if (!!colorCode) dataSpecification.refetch();
    }, [colorCode, wheels]);

    React.useEffect(() => {
        dispatch(setErrorData(!specification ? {
            type: 'model',
            message: t('error_missing_id', 'Model ID or model CODE is missing'),
            redirect: '/'
        } : {}));
    }, [specification]);

    const packagesModalProps = {
        show: resolve?.resolve,
        data: resolve,
        target: option,
        onConfirm: resolveConfirm,
        onCancel: resolveCancel
    };

    const threeSixtyProps = {
        buttonClass: 'button',
        amount: pictures.length,
        autoplay: true,
        spinReverse: true,
        key: pictures,
        images: pictures
    };

    const isThreeSixtyShow = pictures.length && !dataSpecification.isFetching;

    //const { mileagePrice } = useLeasingValue();

    return (<>
        {dataCompatibility.isFetching && (
            <LoadingSpinner />
        )}
        {!errorData?.type ? (<>
            {builderData.eic ? (<>
                <section className={`c-section c-threesixty${selectedTab === 'interiors' ? ' c-threesixty--interior' : ''}`}>
                    <FullScreen handle={handleFullScreen} className="c-threesixty__vehicle c-vehicle">
                        <Container className="c-vehicle__body">
                            <div className="c-vehicle__caption c-caption">
                                <h2 className="c-caption__title">{builderData.model?.name}</h2>
                                <p className="c-caption__descr">{builderData.name}</p>
                                <p className="c-caption__price c-price">
                                    <span className="c-price__title">{t('total_price')}</span>
                                    <strong className="c-price__value">
                                        <Price price={totalPrice} />
                                        <Disclaimer />
                                    </strong>
                                    <span className="c-price__title">{t('leasing_per_month')}</span>
                                </p>
                                <p className="c-caption__btn">
                                    <RewindButton href={modelBackTo} text={t('switch_vehicle')} />
                                </p>
                            </div>
                            {!isSmallerThanLg && (
                                <p className="c-vehicle__action">
                                    {handleFullScreen.active ? (
                                        <Button onClick={handleFullScreen.exit} variant="link">
                                            {t('close_button')}
                                            <CloseFullscreenIco className="btn-ico btn-ico-right" />
                                        </Button>
                                    ) : (
                                        <Button onClick={handleFullScreen.enter} variant="link">
                                            {t('full_view')}
                                            <FullscreenIco className="btn-ico btn-ico-right" />
                                        </Button>
                                    )}
                                </p>
                            )}
                            <LocalizedLink to="/conclusion" className="btn btn-primary btn-summary">
                                {t('summary_button')}
                                <ArrowRight className="btn-ico btn-ico-right" />
                            </LocalizedLink>
                        </Container>
                        {isInteriorTab || !isThreeSixtyShow ? (
                            <figure className="c-vehicle__img">
                                <Image src={
                                    isInteriorTab ?
                                    getActiveInteriorPicture(builderData.interiors, interior) :
                                    imageMain || PlaceholderCar
                                } />
                            </figure>
                        ) : (
                            <ThreeSixty {...threeSixtyProps} />
                        )}
                    </FullScreen>
                    <div className="c-threesixty__nav">
                        <Container>
                            <AssemblySliderSelectors onChange={setOption} />
                        </Container>
                    </div>
                </section>
                <section className="c-section">
                    <Container>
                        <Leasing onChange={handleSetLeasing} />
                        {/*<Packages onChange={setOption} />
                        <Accessories onChange={setOption} />*/}
                    </Container>
                </section>
                <BottomBar action={
                    <LocalizedLink to="/conclusion" className="btn btn-link">
                        {t('summary_button')}
                        <ArrowRight className="btn-ico btn-ico-right" />
                    </LocalizedLink>
                }/>
                <PackagesModal {...packagesModalProps} />
            </>) : (
                <NotFound error={{
                    type: 'builder-data',
                    route: '/select',
                    message: t('error_select_engine', 'Please, select vehicle engine')
                }} />
            )}
        </>) : (
            <NotFound error={errorData} />
        )}
    </>);
};

export default Assemble;
